import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { WizardPill, WizardPills } from '../../ui/wizard-layout';
import { cn } from '../../../lib/utils';
import type { FachItem } from '../../../lib/courses';
import type { Room, RoomUnit } from '../../../lib/rooms';

type StepRoomSelectionProps = Readonly<{
  date: string;
  startTime: string;
  endTime: string;
  roomUnitsLoading: boolean;
  roomUnitsError: string;
  filteredRoomsForFach: Room[];
  roomUnitsForDate: RoomUnit[];
  roomId?: number;
  teacherRoomsOnSelectedDay: Set<number>;
  fachId?: number;
  fachOptions: FachItem[];
  allRoomTags: string[];
  selectedRoomTags: string[];
  roomSearchQuery: string;
  onToggleRoomTag: (tag: string) => void;
  onChangeRoomSearchQuery: (query: string) => void;
  onSelectRoom: (id: number) => void;
  onOpenRoomPreview: (room: Room) => void;
  addMinutes: (hhmm: string, minutes: number) => string;
  overlaps: (startA: string, endA: string, startB: string, endB: string) => boolean;
}>;

const StepRoomSelection = ({
  date,
  startTime,
  endTime,
  roomUnitsLoading,
  roomUnitsError,
  filteredRoomsForFach,
  roomUnitsForDate,
  roomId,
  teacherRoomsOnSelectedDay,
  fachId,
  fachOptions,
  allRoomTags,
  selectedRoomTags,
  roomSearchQuery,
  onToggleRoomTag,
  onChangeRoomSearchQuery,
  onSelectRoom,
  onOpenRoomPreview,
  addMinutes,
  overlaps,
}: StepRoomSelectionProps) => {
  if (!date || !startTime) {
    return (
      <div className="py-2 text-sm text-muted-foreground">Bitte zuerst ein Datum in Schritt 2 wählen.</div>
    );
  }

  const fachName = (fachOptions.find((f) => f.id === fachId)?.name || '').toLowerCase();
  const matchesFach = (tag: string) => {
    const normalizedTag = String(tag || '').trim().toLowerCase();
    if (!fachName || !normalizedTag) return false;
    return normalizedTag === fachName || normalizedTag.includes(fachName) || fachName.includes(normalizedTag);
  };

  return (
    <div className="flex h-full min-h-0 flex-col py-2">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ort</div>
      <div className="mb-3 text-xs text-muted-foreground">
        Wählen Sie einen Raum aus. Relevante Infos und Belegung werden angezeigt.
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <WizardPills className="gap-1.5">
          {allRoomTags.map((tag) => {
            const selected = selectedRoomTags.includes(tag);
            return (
              <button key={tag} type="button" onClick={() => onToggleRoomTag(tag)}>
                <WizardPill active={selected}>{tag}</WizardPill>
              </button>
            );
          })}
        </WizardPills>
      </div>
      <div className="mb-3">
        <Input
          value={roomSearchQuery}
          onChange={(event) => onChangeRoomSearchQuery(event.target.value)}
          placeholder="Raumname oder Adresse suchen…"
          className="h-9 max-w-md"
        />
      </div>
      {roomUnitsLoading ? <div className="mb-3 text-xs text-muted-foreground">Raumbelegung wird geladen…</div> : null}
      {roomUnitsError ? <div className="mb-3 text-xs font-medium text-destructive">{roomUnitsError}</div> : null}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-y-auto pr-1 md:grid-cols-2 xl:grid-cols-3">
          {filteredRoomsForFach.map((room) => {
            const roomUnits = roomUnitsForDate.filter((u) => u.raum_id === room.id);
            const roomEndTime = endTime || addMinutes(startTime, 30);
            const isRoomFree = !roomUnits.some((u) => {
              const s = u.start_zeit.slice(11, 16);
              const e = u.end_zeit.slice(11, 16);
              return overlaps(startTime, roomEndTime, s, e);
            });
            const isSelected = roomId === room.id;
            const uniqueTags = Array.from(
              new Set((room.tags || []).map((tag) => String(tag || '').trim()).filter(Boolean))
            );
            const relevantTags = uniqueTags.filter((tag) => matchesFach(tag));

            return (
              <div
                key={room.id}
                className={cn(
                  'relative rounded-xl border bg-background p-4 text-left min-h-[220px] transition-colors',
                  isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-muted hover:border-primary/40'
                )}
              >
                <button
                  type="button"
                  className="absolute inset-0 rounded-xl"
                  aria-label={`Raum ${room.name} auswählen`}
                  onClick={() => onSelectRoom(room.id)}
                />
                <div className="relative z-10 pointer-events-none flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{room.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {room.address || [room.address_street, room.address_house_number, room.address_postal_code, room.address_city].filter(Boolean).join(' ') || 'Keine Adresse hinterlegt'}
                    </div>
                    <div className="mt-2">
                      <WizardPills className="gap-1.5">
                        <WizardPill
                          active={isRoomFree}
                          className={cn(isRoomFree ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white')}
                        >
                          {isRoomFree ? 'frei' : 'belegt'}
                        </WizardPill>
                        {isRoomFree && teacherRoomsOnSelectedDay.has(room.id) ? (
                          <WizardPill active={false} className="bg-indigo-50 text-indigo-700 border border-indigo-200">
                            Lehrer ist heute hier
                          </WizardPill>
                        ) : null}
                        {relevantTags.slice(0, 6).map((tag) => (
                          <WizardPill key={tag} active={true}>
                            {tag}
                          </WizardPill>
                        ))}
                      </WizardPills>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="pointer-events-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenRoomPreview(room);
                      }}
                    >
                      Details
                    </Button>
                  </div>
                </div>
                {!isRoomFree && startTime ? (
                  <div className="mt-3 text-xs text-red-700">Hinweis: Raum ist zur gewählten Zeit belegt.</div>
                ) : (
                  <div className="mt-3 text-xs text-muted-foreground">Raum ist zur gewählten Zeit frei.</div>
                )}
              </div>
            );
          })}
      </div>
      {filteredRoomsForFach.length === 0 ? (
        <div className="mt-3 text-xs text-muted-foreground">
          Keine Räume mit den aktuellen Filtern gefunden.
        </div>
      ) : null}
    </div>
  );
};

export default StepRoomSelection;
